// frontend/source/portal/pages/AdminArtworkDetailsPage.js
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import adminApi from "../../services/adminapi";
import api from "../../services/api";
import AdminArtworkDisplay from "../components/AdminArtworkDisplay";
import AdminArtworkDetails from "../components/AdminArtworkDetails";
import NavigationButtons from "../../components/ArtworkPage/NavigationButtons";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import useKeyboardNavigation from "../../hooks/useKeyboardNavigation";
import "./AdminArtworkDetailsPage.css";

const AdminArtworkDetailsPage = () => {
  const { artworkId, categoryId, tagId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [currentArtwork, setCurrentArtwork] = useState(null);
  const [artworkList, setArtworkList] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [categoryName, setCategoryName] = useState("");
  const [tagName, setTagName] = useState("");

  useEffect(() => {
    const loadArtwork = async () => {
      try {
        setLoading(true);
        console.log("AdminArtworkDetailsPage", categoryId, tagId);

        // Load category and tag details
        const [categoryData, tagData] = await Promise.all([
          api.getCategoryById(categoryId),
          api.getArtTagById(tagId),
        ]);
        setCategoryName(categoryData.name);
        setTagName(tagData.name);

        if (location.state?.artworkList) {
          const list = location.state.artworkList;
          const index = list.findIndex((a) => a._id === artworkId);
          setArtworkList(list);
          setCurrentIndex(index);
          setCurrentArtwork(list[index]);
        } else {
          // Load all artworks for this tag
          const artworksData = await api.getArtworksByTag(
            categoryId,
            tagId,
            1,
            0
          );
          const list = artworksData.artworks || artworksData;
          const index = list.findIndex((a) => a._id === artworkId);
          setArtworkList(list);
          setCurrentIndex(index);
          setCurrentArtwork(list[index]);
        }
      } catch (err) {
        console.error("Error loading artwork:", err);
      } finally {
        setLoading(false);
      }
    };

    loadArtwork();
  }, [artworkId, categoryId, tagId, location.state]);

  const goToPrevious = () => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      const newArtwork = artworkList[newIndex];
      navigate(
        `/portal/category/${categoryId}/tag/${tagId}/artwork/${newArtwork._id}`,
        {
          state: { artworkList, categoryName, tagName },
          replace: true,
        }
      );
    }
  };

  const goToNext = () => {
    if (currentIndex < artworkList.length - 1) {
      const newIndex = currentIndex + 1;
      const newArtwork = artworkList[newIndex];
      navigate(
        `/portal/category/${categoryId}/tag/${tagId}/artwork/${newArtwork._id}`,
        {
          state: { artworkList, categoryName, tagName },
          replace: true,
        }
      );
    }
  };

  const handleBackToArtworks = () => {
    navigate(`/portal/category/${categoryId}/tag/${tagId}`);
  };

  const handleUpdateArtwork = async (updates) => {
    try {
      const updated = await adminApi.updateArtwork(currentArtwork._id, updates);
      setCurrentArtwork(updated);
      // Update in the list as well
      setArtworkList(
        artworkList.map((art) => (art._id === updated._id ? updated : art))
      );
    } catch (err) {
      console.error("Failed to update artwork:", err);
      throw err;
    }
  };

  useKeyboardNavigation(
    goToPrevious,
    goToNext,
    currentIndex,
    artworkList.length
  );

  if (loading || !currentArtwork) {
    return <LoadingSpinner message="Loading artwork..." />;
  }

  return (
    <div>
      <NavigationButtons
        onPrevious={goToPrevious}
        onNext={goToNext}
        hasPrevious={currentIndex > 0}
        hasNext={currentIndex < artworkList.length - 1}
      />

      <div className="admin-artwork-main">
        <div className="admin-artwork-content">
          {/* Breadcrumb Navigation */}
          <nav className="admin-artwork-breadcrumb">
            <button
              className="admin-artwork-breadcrumb-link"
              onClick={handleBackToArtworks}
              aria-label={`Back to ${categoryName} ${tagName} artworks`}
            >
              <svg
                className="admin-artwork-breadcrumb-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              {categoryName} - {tagName}
            </button>
            <span className="admin-artwork-breadcrumb-separator">/</span>
            <span className="admin-artwork-breadcrumb-current">
              {currentArtwork.title}
            </span>
          </nav>

          <AdminArtworkDisplay
            artwork={currentArtwork}
            onUpdate={handleUpdateArtwork}
          />
          <AdminArtworkDetails
            artwork={currentArtwork}
            currentIndex={currentIndex}
            totalCount={artworkList.length}
            onUpdate={handleUpdateArtwork}
          />
        </div>
      </div>
    </div>
  );
};

export default AdminArtworkDetailsPage;
